<!-- eslint-disable max-len -->
<i18n lang="yaml">
  en:
    title: Contact us
    description: 'Our team is available for any questions.<br /> Please fill out the form below and we will get back to you shortly!  '
    formType:
      label: I’m
      individual: Person
      company: Company
    formSubmit: Submit
    error: An error occured, please try again later.
    success: Your message has been sent successfully, thank you!
    home: Back to home
    firstname: Firstname
    lastname: Surname
  fr:
    title: Contactez-nous
    description: 'Notre équipe est à votre écoute et prête à répondre à toutes 
      vos questions.<br /> - Par téléphone <a class="type-decoration-none" href="tel:+33184730378">
      <span class="color-green type-weight-700">+33 1 84 73 03 78</span></a> <br /> - Ou en nous
      écrivant ci-dessous :'
    formType:
      label: Je suis
      individual: Un particulier
      company: Un professionnel
    formSubmit: Envoyer
    error: Une erreur s'est produite, réessayez plus tard, ou bien appelez-nous !
    success: Votre message a bien été envoyé, merci !
    home: Revenir à l'accueil
    firstname: Prénom
    lastname: Nom
</i18n>
<!-- eslint-enable max-len -->

<template>
  <form class="contact" @submit.prevent="submitHandler">
    <div class="grid">
      <default-modal
        class="space-pb-8"
        name="contact"
        :title="$t('title')"
        @closed="$router.push(localePath('index'))"
      >
        <template v-slot:header>
          <div class="grid__row">
            <div
              class="grid__push-1--xxs grid__col-10--xxs grid__push-2--m grid__col-8--m type-title-secondary type-weight-700 space-pb-3 space-mb-5 border-bottom"
            >{{ $t('title') }}</div>
          </div>
          <div class="grid__row">
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="grid__push-1--xxs grid__col-10--xxs grid__push-2--m grid__col-8--m type-body space-pb-3 space-mb-2"
              v-html="$t('description')"
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </template>
        <template v-slot:content>
          <div class="grid grid--nested position-relative" style="overflow: hidden;">
            <div class="grid__row">
              <form-radio
                v-if="$route.query.activeInputList !== 'inputsCompany'"
                v-model="activeInputList"
                :inline="true"
                label-classes="
                  grid__col-12--xxs
                  grid__col-2--m
                  space-mb-3
                "
                :value="'yes'"
                :name="'yes'"
                :options="[
                  {
                    label: $t('formType.individual'),
                    value: 'inputsIndividual',
                    // eslint-disable-next-line max-len
                    classes: 'grid__push-2--xxs grid__col-12--xxs grid__push-0--m grid__col-3--m grid__col-2--l space-mr-0--xxs'
                  },
                  {
                    label: $t('formType.company'),
                    value: 'inputsCompany',
                    // eslint-disable-next-line max-len
                    classes: 'grid__push-2--xxs grid__col-12--xxs grid__push-0--m grid__col-3--m grid__col-2--l space-mr-0--xxs'
                  }
                ]"
              />
              <div class="grid__push-1--xxs grid__col-10--xxs grid__push-2--m grid__col-8--m">
                <ul class="form__list">
                  <li
                    v-for="(input, key) in inputs[activeInputList]"
                    :key="key"
                    :class="input.class"
                  >
                    <component
                      :is="input.component"
                      v-model="input.value"
                      :label="$t(input.label)"
                      :input="input"
                      v-bind="input.props || {}"
                      :attr="input.attr"
                    />
                  </li>
                </ul>
                <div class="flex align-center space-pb-10">
                  <div class="type-align-right space-ml-auto">
                    <button
                      type="submit"
                      class="btn btn--primary"
                      :disabled="isSubmitting"
                    >{{ $t('formSubmit') }}</button>
                  </div>
                </div>
              </div>
              <div v-if="error || success" class="form__message">
                <div
                  :class="{
                    'color-red': error,
                    'color-green': success,
                  }"
                >
                  <p v-if="error" class="type-weight-700 space-mb-3">{{ error }}</p>
                  <p v-else class="type-weight-700 space-mb-3">{{ success }}</p>
                  <nuxt-link :to="localePath('index')" class="btn btn--primary">{{ $t('home') }}</nuxt-link>
                </div>
              </div>
            </div>
          </div>
        </template>
      </default-modal>
    </div>
  </form>
</template>

<script>
import DefaultModal from '~/components/DefaultModal';
import FormText from '~/components/Form/FormText';
import FormTextarea from '~/components/Form/FormTextarea';
import FormSelect from '~/components/Form/FormSelect';
import FormRadio from '~/components/Form/FormRadio';

export default {
  components: {
    DefaultModal,
    FormText,
    FormTextarea,
    FormSelect,
    FormRadio,
  },
  data() {
    return {
      error: '',
      success: '',
      isSubmitting: false,
      activeInputList: 'inputsIndividual',
      inputs: {
        inputsIndividual: {
          firstname: {
            component: 'FormText',
            label: 'firstname',
            value: '',
            class: 'form__item form__item--small',
            attr: {
              name: 'firstname',
              placeholder: 'Roger',
              required: true,
            },
          },
          lastname: {
            component: 'FormText',
            label: 'lastname',
            value: '',
            class: 'form__item form__item--small',
            attr: {
              name: 'lastname',
              placeholder: 'Rabbit',
              required: true,
            },
          },
          email: {
            component: 'FormText',
            label: 'Email',
            value: '',
            class: 'form__item',
            attr: {
              name: 'email',
              placeholder: 'roger@rabbit.com',
              required: true,
              type: 'email',
            },
          },
          phone: {
            component: 'FormText',
            label: 'Téléphone (optionnel)',
            value: '',
            class: 'form__item form__item--small',
            attr: {
              name: 'phone',
              placeholder: '06 12 34 56 78',
              required: false,
            },
          },
          country: {
            component: 'FormText',
            label: 'Pays (optionnel)',
            value: '',
            class: 'form__item form__item--small',
            attr: {
              name: 'country',
              placeholder: 'France',
              required: false,
            },
          },
          object: {
            component: 'FormSelect',
            value: '',
            label: 'Objet',
            class: 'form__item',
            attr: {
              name: 'objet',
              required: true,
            },
            props: {
              config: {
                placeholder: {
                  en: 'Select a subject',
                  fr: 'Séléctionnez un objet',
                },
              },
              options: [
                {
                  label: 'Contact',
                  value: 'contact@ween.fr',
                },
                {
                  label: 'Support',
                  value: 'support@ween.fr',
                },
              ],
            },
          },
          message: {
            component: 'FormTextarea',
            label: 'Votre message',
            value: '',
            class: 'form__item',
            attr: {
              name: 'message',
              placeholder: 'Votre message...',
              required: true,
            },
          },
        },
        inputsCompany: {
          firstname: {
            component: 'FormText',
            label: 'Prénom',
            value: '',
            class: 'form__item form__item--small',
            attr: {
              name: 'firstname',
              placeholder: 'Prénom',
              required: true,
            },
          },
          lastname: {
            component: 'FormText',
            label: 'Nom',
            value: '',
            class: 'form__item form__item--small',
            attr: {
              name: 'lastname',
              placeholder: 'Nom',
              required: true,
            },
          },
          company: {
            component: 'FormText',
            label: 'Société',
            value: '',
            class: 'form__item',
            attr: {
              name: 'company',
              placeholder: 'Nom de la société',
              required: true,
            },
          },
          role: {
            component: 'FormText',
            label: 'Fonction',
            value: '',
            class: 'form__item',
            attr: {
              name: 'role',
              placeholder: 'Votre fonction',
              required: true,
            },
          },
          email: {
            component: 'FormText',
            label: 'Email',
            value: '',
            class: 'form__item',
            attr: {
              name: 'email',
              placeholder: 'Email',
              required: true,
              type: 'email',
            },
          },
          phone: {
            component: 'FormText',
            label: 'Téléphone',
            value: '',
            class: 'form__item form__item--small',
            attr: {
              name: 'phone',
              placeholder: 'Téléphone',
              required: true,
            },
          },
          country: {
            component: 'FormText',
            label: 'Pays (optionnel)',
            value: '',
            class: 'form__item form__item--small',
            attr: {
              name: 'country',
              placeholder: 'Pays',
              required: false,
            },
          },
          object: {
            component: 'FormSelect',
            value: '',
            label: 'Objet',
            class: 'form__item',
            attr: {
              name: 'objet',
              required: true,
            },
            props: {
              config: {
                placeholder: {
                  en: 'Select a subject',
                  fr: 'Séléctionnez un objet',
                },
              },
              options: [
                {
                  label: 'Contact',
                  value: 'contact@ween.fr',
                },
                {
                  label: 'Support',
                  value: 'support@ween.fr',
                },
              ],
            },
          },
          message: {
            component: 'FormTextarea',
            label: 'Votre message',
            value: '',
            class: 'form__item',
            attr: {
              name: 'message',
              placeholder: 'Votre message...',
              required: true,
            },
          },
        },
      },
    };
  },
  mounted() {
    this.$modal.show('contact');

    if (this.$route.query.activeInputList === 'inputsCompany') {
      this.activeInputList = 'inputsCompany';
    }
  },
  methods: {
    getFormData() {
      const fields =
        this.activeInputList === 'inputsCompany'
          ? this.inputs.inputsCompany
          : this.inputs.inputsIndividual;
      return Object.entries(fields).reduce((data, [name, config]) => {
        data[name] = config.value;
        return data;
      }, {});
    },
    async submitHandler() {
      // Prevent double submissions
      if (this.isSubmitting) {
        return;
      }

      const data = this.getFormData();
      this.error = '';
      this.message = '';
      this.isSubmitting = true;

      try {
        await fetch(
          'https://plugin.ween.fr/mailer.php',
          {
            method: 'POST',
            body: JSON.stringify(data),
          }
        );
      } catch (err) {
        this.$sentry.captureException(new Error(err));
        const message = `Failed to send mail: ${JSON.stringify(data)}`;
        this.$sentry.captureMessage(message);
      }

      this.success = this.$t('success');
      this.isSubmitting = false;
    },
  },
};
</script>


<style lang="scss">
// @todo move this to @import '~assets/scss/form/layout';

/*------------------------------------*
    Form layout global styles
  *------------------------------------*/
.form__list {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.form__item {
  width: 100%;
  margin-bottom: s(3);
}

/*------------------------------------*
    inputs that take 50% of the width
  *------------------------------------*/
.form__item--small {
  @media #{md('s')} {
    width: calc(50% - #{s(2)});
  }
}

/*------------------------------------*
    Labels
  *------------------------------------*/
.form__label {
  @include fz('btn');
  display: block;
  padding-bottom: s(1);
  font-weight: 700;
  line-height: 1.333;
}

.form__message {
  z-index: l(default);
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  text-align: center;
  background-color: c('white');
}
</style>
